import { FileUp } from 'lucide-react';
import { useCallback, useContext } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import Badge from '@/components/Badge/Badge';
import { Button } from '@/components/ui/button';
import GlobalContext from '@/context/GlobalContext';
import { SetStatus } from '@/Enums/enum';
import { PDFIcon } from '@/icons/index';
import { StyledH3 } from '@/Shared/Typography/typography';
import { getFormattedDate } from '@/utils';
import { CommentDrawerTrigger } from '../CourseComments/CommentDrawer';
import CourseEditContext from '../CourseEditContext';
import { StyledCourseHeader } from './CourseHeader.Styles';
import { CoursePdf, downLoadPDF } from './CoursePdf';

interface CourseHeaderProps {
	onImportClick?: () => void;
}

const CourseHeader = ({ onImportClick }: CourseHeaderProps) => {
	const { isApprovalMode, courseData, courseDraftData } = useContext(CourseEditContext);
	const { siteSettings } = useContext(GlobalContext);
	const {
		is_saved_draft: isSavedDraft,
		course_name: courseName,
		last_updated: lastUpdated,
		course_id: courseId,
		submission_id: submissionId,
		submission: courseSubmission,
		is_archived: isArchived,
	} = courseData;
	const navigate = useNavigate();
	const { pathname, hash, state } = useLocation();
	const returnTo = (state as { from?: string; id?: string } | null)?.from;

	const isPendingApproval = useCallback(() => {
		if (courseSubmission) {
			return Object.values(courseSubmission).every((status) => status !== SetStatus.APPROVED);
		}
		return false;
	}, [courseSubmission]);

	const renderBadge = () => {
		if (isSavedDraft && !isApprovalMode) {
			return <Badge text="Draft" style={{ marginRight: 'var(--spacing-4)' }} />;
		}

		if (isPendingApproval() && !isApprovalMode) {
			return <Badge text="Approval Pending" style={{ marginRight: 'var(--spacing-4)' }} />;
		}
		return null;
	};

	const navigateBack = () => {
		if (returnTo) {
			navigate(returnTo);
			return;
		}
		if (pathname.includes('submissions')) {
			navigate('/dashboard/submissions', {
				state: {
					id: submissionId,
				},
			});
			return;
		}
		// add search param of showArchived and courseId to state
		// courseId allows page to scroll to previously selected course
		navigate(
			{
				pathname: '/dashboard/courses',
				search: createSearchParams({ showArchived: `${isArchived}` }).toString(),
			},
			{
				state: {
					id: courseId,
				},
			}
		);
	};

	return (
		<StyledCourseHeader>
			<div className="flex items-center gap-2 mb-4">
				<Button size="xs" onClick={navigateBack}>
					Back
				</Button>
				{!(isApprovalMode || hash.includes('lesson-plans')) && (
					<Button
						variant="secondary"
						size="xs"
						className="inline-flex items-center p-0 text-xs relative transition-colors duration-300 ease-in-out overflow-hidden group grid-cols-[auto,50px]"
						onClick={() => downLoadPDF(siteSettings?.logo ?? '')}
					>
						<div className="p-2">Download</div>
						<div className="bg-primary p-2 rounded-[0 3px 3px 0]">
							<PDFIcon />
						</div>
					</Button>
				)}
				{onImportClick && (
					<button
						type="button"
						onClick={onImportClick}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 hover:bg-secondary transition-colors"
					>
						<FileUp size={13} />
						Import from File
					</button>
				)}
				{/* Comments trigger sits inline with Back/Download on mobile */}
				<CommentDrawerTrigger />
			</div>
			<div className="title">
				<div>
					<StyledH3 mb="8px" mr="16px">
						{courseName}
					</StyledH3>
				</div>
				<div>
					{isArchived ? (
						<Badge
							text="Archived"
							style={{ marginRight: 'var(--spacing-4)' }}
							color="var(--yellow-200)"
						/>
					) : null}

					{renderBadge()}
				</div>
				{lastUpdated && (
					<time className="date" dateTime={lastUpdated}>
						Last Updated {getFormattedDate(lastUpdated)}
					</time>
				)}
			</div>
			{!isApprovalMode && (
				<CoursePdf courseDraftData={courseDraftData} courseName={courseData.course_name} />
			)}
		</StyledCourseHeader>
	);
};
export default CourseHeader;
